/**
 * Production System Health Dashboard
 * 
 * Comprehensive real-time monitoring dashboard for life-critical system
 * Integrates frontend metrics, backend health, and operational alerts
 */

import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Center,
    Code,
    Grid,
    Group,
    JsonInput,
    Modal,
    Progress,
    RingProgress,
    ScrollArea,
    Stack,
    Table,
    Tabs,
    Text,
    ThemeIcon,
    Timeline,
    Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
    IconActivity,
    IconAlertTriangle,
    IconCheck,
    IconDeviceAnalytics,
    IconDownload,
    IconHeart,
    IconRefresh,
    IconServer,
    IconTrendingUp,
    IconX
} from '@tabler/icons-react';
import React, { useCallback, useEffect, useState } from 'react';
// TODO: Fix graphql-client package exports - using local types for now
import { MetricsAlert, productionMetrics, ProductionMetrics } from '@ferp/graphql-client';

// Local type definitions until graphql-client exports are fixed
interface TenantMetrics {
  requests: number;
  errors: number;
  avgResponseTime: number;
  lastActivity: number;
}

interface OperationMetrics {
  operationName: string;
  operationType: 'query' | 'mutation' | 'subscription';
  count: number;
  errors: number;
  avgResponseTime: number;
  p95ResponseTime: number;
  cacheHitRate: number;
}

interface SystemHealthProps {
  tenant?: string;
  refreshInterval?: number; // milliseconds
}

interface BackendHealth {
  status: 'healthy' | 'degraded' | 'critical' | 'unknown';
  services: {
    database: 'up' | 'down' | 'degraded';
    cache: 'up' | 'down' | 'degraded';
    graphql: 'up' | 'down' | 'degraded';
    auth: 'up' | 'down' | 'degraded';
  };
  metrics: {
    uptime: number;
    memory_usage: number;
    cpu_usage: number;
    disk_usage: number;
    active_connections: number;
  };
  lastCheck: Date;
}

export const ProductionSystemHealthDashboard: React.FC<SystemHealthProps> = ({
  tenant = 'system',
  refreshInterval = 30000, // 30 seconds
}) => {
  const [metrics, setMetrics] = useState<ProductionMetrics | null>(null);
  const [alerts, setAlerts] = useState<MetricsAlert[]>([]);
  const [backendHealth, setBackendHealth] = useState<BackendHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);
  const [exportData, setExportData] = useState<string>('');

  // Fetch metrics and health data
  const refreshData = useCallback(async () => {
    try {
      // Get frontend metrics
      const frontendMetrics = productionMetrics.getProductionMetrics();
      setMetrics(frontendMetrics);

      // Get recent alerts
      const recentAlerts = productionMetrics.getRecentAlerts(Date.now() - 3600000); // Last hour
      setAlerts(recentAlerts);

      // Simulate backend health check (replace with actual API call)
      const mockBackendHealth: BackendHealth = {
        status: Math.random() > 0.1 ? 'healthy' : 'degraded',
        services: {
          database: Math.random() > 0.05 ? 'up' : 'degraded',
          cache: Math.random() > 0.03 ? 'up' : 'degraded',
          graphql: Math.random() > 0.02 ? 'up' : 'degraded',
          auth: Math.random() > 0.01 ? 'up' : 'degraded',
        },
        metrics: {
          uptime: Math.floor(Math.random() * 30) + 1, // 1-30 days
          memory_usage: Math.floor(Math.random() * 40) + 50, // 50-90%
          cpu_usage: Math.floor(Math.random() * 30) + 10, // 10-40%
          disk_usage: Math.floor(Math.random() * 20) + 60, // 60-80%
          active_connections: Math.floor(Math.random() * 500) + 100, // 100-600
        },
        lastCheck: new Date(),
      };

      // In production, fetch from actual health endpoint:
      // const response = await fetch('/api/health/status');
      // const backendHealth = await response.json();

      setBackendHealth(mockBackendHealth);
      setLastRefresh(new Date());

    } catch (error) {
      console.error('Failed to fetch health data:', error);
      notifications.show({
        title: 'Health Check Failed',
        message: 'Unable to fetch system health data',
        color: 'red',
        icon: <IconX size={16} />,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh data
  useEffect(() => {
    refreshData();
    const interval = setInterval(refreshData, refreshInterval);
    return () => clearInterval(interval);
  }, [refreshData, refreshInterval]);

  // Alert monitoring
  useEffect(() => {
    const unsubscribe = productionMetrics.onAlert((alert) => {
      setAlerts(prev => [alert, ...prev.slice(0, 49)]); // Keep last 50 alerts
      
      if (alert.level === 'critical') {
        notifications.show({
          title: 'Critical System Alert',
          message: alert.message,
          color: 'red',
          autoClose: false,
          icon: <IconAlertTriangle size={16} />,
        });
      }
    });

    return unsubscribe;
  }, []);

  const handleExportMetrics = useCallback(() => {
    const exportData = productionMetrics.exportMetrics();
    setExportData(exportData);
    openExportModal();
  }, [openExportModal]);

  const handleDownloadExport = useCallback(() => {
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `system-health-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [exportData]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'up':
        return 'green';
      case 'degraded':
        return 'orange';
      case 'critical':
      case 'down':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getHealthScoreColor = (score: number) => {
    if (score >= 90) return 'green';
    if (score >= 70) return 'orange';
    return 'red';
  };

  if (loading) {
    return (
      <Center h={400}>
        <Stack ta="center" gap="md">
          <IconActivity size={48} color="blue" />
          <Text size="lg">Loading system health data...</Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="apart">
        <div>
          <Title order={2}>
            <Group gap="xs">
              <IconHeart size={28} color="red" />
              Production System Health
            </Group>
          </Title>
          <Text size="sm" color="dimmed">
            Real-time monitoring and observability • Last updated: {lastRefresh.toLocaleString()}
          </Text>
        </div>
        <Group gap="sm">
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleExportMetrics}
            variant="light"
            size="sm"
          >
            Export Metrics
          </Button>
          <ActionIcon
            variant="light"
            onClick={refreshData}
            loading={loading}
            size="lg"
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>
      </Group>

      {/* Critical Alerts */}
      {alerts.filter(a => a.level === 'critical').length > 0 && (
        <Alert
          icon={<IconAlertTriangle size={20} />}
          title="Critical Alerts"
          color="red"
          variant="filled"
        >
          <Stack gap="xs">
            {alerts.filter(a => a.level === 'critical').slice(0, 3).map((alert, i) => (
              <Text key={i} size="sm">
                • {alert.message}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Main Health Overview */}
      <Grid>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Card shadow="sm" padding="lg" withBorder>
            <Group justify="center" mb="md">
              <RingProgress
                size={120}
                thickness={12}
                sections={[{ 
                  value: metrics?.healthScore || 0, 
                  color: getHealthScoreColor(metrics?.healthScore || 0) 
                }]}
                label={
                  <Center>
                    <div style={{ textAlign: 'center' }}>
                      <Text size="xs" color="dimmed" mb={4}>Health Score</Text>
                      <Text size="xl" fw={700}>
                        {(metrics?.healthScore || 0).toFixed(1)}
                      </Text>
                    </div>
                  </Center>
                }
              />
            </Group>
            <Stack gap="xs">
              <Group justify="apart">
                <Text size="sm" color="dimmed">Overall Status</Text>
                <Badge 
                  color={getStatusColor(backendHealth?.status || 'unknown')}
                  variant="light"
                >
                  {backendHealth?.status?.toUpperCase() || 'UNKNOWN'}
                </Badge>
              </Group>
              <Group justify="apart">
                <Text size="sm" color="dimmed">Circuit Breaker</Text>
                <Badge 
                  color={metrics?.circuitBreakerState === 'CLOSED' ? 'green' : 'red'}
                  variant="light"
                >
                  {metrics?.circuitBreakerState || 'UNKNOWN'}
                </Badge>
              </Group>
            </Stack>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 8 }}>
          <Grid>
            <Grid.Col span={6}>
              <Card shadow="sm" padding="md" withBorder>
                <Group gap="xs" mb="xs">
                  <ThemeIcon color="blue" variant="light" size="sm">
                    <IconTrendingUp size={16} />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>Request Metrics</Text>
                </Group>
                <Stack gap="xs">
                  <Group justify="apart">
                    <Text size="xs" color="dimmed">Total Requests</Text>
                    <Text size="sm" fw={500}>{metrics?.totalRequests || 0}</Text>
                  </Group>
                  <Group justify="apart">
                    <Text size="xs" color="dimmed">Error Rate</Text>
                    <Text size="sm" fw={500} color={metrics?.errorRate && metrics.errorRate > 0.05 ? 'red' : 'green'}>
                      {((metrics?.errorRate || 0) * 100).toFixed(2)}%
                    </Text>
                  </Group>
                  <Group justify="apart">
                    <Text size="xs" color="dimmed">P95 Response</Text>
                    <Text size="sm" fw={500}>
                      {(metrics?.p95ResponseTime || 0).toFixed(0)}ms
                    </Text>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={6}>
              <Card shadow="sm" padding="md" withBorder>
                <Group gap="xs" mb="xs">
                  <ThemeIcon color="green" variant="light" size="sm">
                    <IconServer size={16} />
                  </ThemeIcon>
                  <Text size="sm" fw={500}>Backend Health</Text>
                </Group>
                <Stack gap="xs">
                  <Group justify="apart">
                    <Text size="xs" color="dimmed">Uptime</Text>
                    <Text size="sm" fw={500}>
                      {backendHealth?.metrics.uptime || 0} days
                    </Text>
                  </Group>
                  <Group justify="apart">
                    <Text size="xs" color="dimmed">Memory</Text>
                    <Group gap="xs">
                      <Progress 
                        value={backendHealth?.metrics.memory_usage || 0} 
                        size="xs" 
                        style={{ flex: 1 }}
                        color={backendHealth?.metrics.memory_usage && backendHealth.metrics.memory_usage > 85 ? 'red' : 'blue'}
                      />
                      <Text size="xs">{backendHealth?.metrics.memory_usage || 0}%</Text>
                    </Group>
                  </Group>
                  <Group justify="apart">
                    <Text size="xs" color="dimmed">Connections</Text>
                    <Text size="sm" fw={500}>
                      {backendHealth?.metrics.active_connections || 0}
                    </Text>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        </Grid.Col>
      </Grid>

      {/* Detailed Tabs */}
      <Tabs defaultValue="services" variant="outline">
        <Tabs.List>
          <Tabs.Tab value="services" leftSection={<IconServer size={16} />}>
            Services Status
          </Tabs.Tab>
          <Tabs.Tab value="metrics" leftSection={<IconDeviceAnalytics size={16} />}>
            Performance Metrics
          </Tabs.Tab>
          <Tabs.Tab value="alerts" leftSection={<IconAlertTriangle size={16} />}>
            Alerts ({alerts.length})
          </Tabs.Tab>
          <Tabs.Tab value="operations" leftSection={<IconActivity size={16} />}>
            Operations
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="services" pt="md">
          <Grid>
            {backendHealth && Object.entries(backendHealth.services).map(([service, status]) => (
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }} key={service}>
                <Card shadow="sm" padding="md" withBorder>
                  <Group gap="xs">
                    <ThemeIcon 
                      color={getStatusColor(status)} 
                      variant="light" 
                      size="lg"
                    >
                      {status === 'up' ? <IconCheck size={20} /> : 
                       status === 'degraded' ? <IconAlertTriangle size={20} /> : 
                       <IconX size={20} />}
                    </ThemeIcon>
                    <div>
                      <Text fw={500} tt="capitalize">
                        {service.replace('_', ' ')}
                      </Text>
                      <Badge 
                        color={getStatusColor(status)} 
                        variant="light" 
                        size="sm"
                      >
                        {status.toUpperCase()}
                      </Badge>
                    </div>
                  </Group>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="metrics" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card shadow="sm" padding="md" withBorder>
                <Title order={4} mb="md">Frontend Metrics</Title>
                <Table>
                  <tbody>
                    <tr>
                      <td>Total Requests</td>
                      <td>{metrics?.totalRequests || 0}</td>
                    </tr>
                    <tr>
                      <td>Success Rate</td>
                      <td>{((1 - (metrics?.errorRate || 0)) * 100).toFixed(2)}%</td>
                    </tr>
                    <tr>
                      <td>Average Response Time</td>
                      <td>{(metrics?.averageResponseTime || 0).toFixed(2)}ms</td>
                    </tr>
                    <tr>
                      <td>P95 Response Time</td>
                      <td>{(metrics?.p95ResponseTime || 0).toFixed(2)}ms</td>
                    </tr>
                    <tr>
                      <td>P99 Response Time</td>
                      <td>{(metrics?.p99ResponseTime || 0).toFixed(2)}ms</td>
                    </tr>
                    <tr>
                      <td>Cache Hit Rate</td>
                      <td>{((metrics?.cacheHitRate || 0) * 100).toFixed(2)}%</td>
                    </tr>
                  </tbody>
                </Table>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card shadow="sm" padding="md" withBorder>
                <Title order={4} mb="md">Backend Resources</Title>
                <Table>
                  <tbody>
                    <tr>
                      <td>CPU Usage</td>
                      <td>
                        <Group gap="xs">
                          <Progress 
                            value={backendHealth?.metrics.cpu_usage || 0} 
                            size="sm" 
                            style={{ flex: 1 }}
                            color={backendHealth?.metrics.cpu_usage && backendHealth.metrics.cpu_usage > 80 ? 'red' : 'blue'}
                          />
                          <Text size="sm">{backendHealth?.metrics.cpu_usage || 0}%</Text>
                        </Group>
                      </td>
                    </tr>
                    <tr>
                      <td>Memory Usage</td>
                      <td>
                        <Group gap="xs">
                          <Progress 
                            value={backendHealth?.metrics.memory_usage || 0} 
                            size="sm" 
                            style={{ flex: 1 }}
                            color={backendHealth?.metrics.memory_usage && backendHealth.metrics.memory_usage > 85 ? 'red' : 'blue'}
                          />
                          <Text size="sm">{backendHealth?.metrics.memory_usage || 0}%</Text>
                        </Group>
                      </td>
                    </tr>
                    <tr>
                      <td>Disk Usage</td>
                      <td>
                        <Group gap="xs">
                          <Progress 
                            value={backendHealth?.metrics.disk_usage || 0} 
                            size="sm" 
                            style={{ flex: 1 }}
                            color={backendHealth?.metrics.disk_usage && backendHealth.metrics.disk_usage > 90 ? 'red' : 'blue'}
                          />
                          <Text size="sm">{backendHealth?.metrics.disk_usage || 0}%</Text>
                        </Group>
                      </td>
                    </tr>
                    <tr>
                      <td>Active Connections</td>
                      <td>{backendHealth?.metrics.active_connections || 0}</td>
                    </tr>
                  </tbody>
                </Table>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="alerts" pt="md">
          {alerts.length === 0 ? (
            <Alert icon={<IconCheck size={16} />} title="All Clear" color="green">
              <Text size="sm">No recent alerts detected. System is operating normally.</Text>
            </Alert>
          ) : (
            <ScrollArea h={400}>
              <Timeline active={-1} lineWidth={2}>
                {alerts.slice(0, 20).map((alert, index) => (
                  <Timeline.Item
                    key={index}
                    bullet={
                      <ThemeIcon
                        size={20}
                        radius="xl"
                        variant="filled"
                        color={alert.level === 'critical' ? 'red' : 'orange'}
                      >
                        <IconAlertTriangle size={12} />
                      </ThemeIcon>
                    }
                    title={
                      <Group gap="xs">
                        <Text fw={500}>{alert.message}</Text>
                        <Badge 
                          color={alert.level === 'critical' ? 'red' : 'orange'}
                          variant="light"
                          size="xs"
                        >
                          {alert.level.toUpperCase()}
                        </Badge>
                      </Group>
                    }
                  >
                    <Text size="xs" color="dimmed">
                      {alert.metric}: {alert.value} (threshold: {alert.threshold})
                    </Text>
                    <Text size="xs" color="dimmed">
                      {new Date(alert.timestamp).toLocaleString()}
                    </Text>
                  </Timeline.Item>
                ))}
              </Timeline>
            </ScrollArea>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="operations" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card shadow="sm" padding="md" withBorder>
                <Title order={4} mb="md">Top Operations</Title>
                <Table>
                  <thead>
                    <tr>
                      <th>Operation</th>
                      <th>Requests</th>
                      <th>Avg Time</th>
                      <th>Errors</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics && Object.entries(metrics.operationMetrics)
                      .sort(([,a], [,b]) => (b as OperationMetrics).count - (a as OperationMetrics).count)
                      .slice(0, 10)
                      .map(([name, op]) => {
                        const operation = op as OperationMetrics;
                        return (
                        <tr key={name}>
                          <td>
                            <Code>{operation.operationName}</Code>
                          </td>
                          <td>{operation.count}</td>
                          <td>{operation.avgResponseTime.toFixed(0)}ms</td>
                          <td>
                            <Text color={operation.errors > 0 ? 'red' : 'green'} size="sm">
                              {operation.errors}
                            </Text>
                          </td>
                        </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card shadow="sm" padding="md" withBorder>
                <Title order={4} mb="md">Tenant Activity</Title>
                <Table>
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Requests</th>
                      <th>Errors</th>
                      <th>Last Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics && Object.entries(metrics.tenantMetrics)
                      .sort(([,a], [,b]) => (b as TenantMetrics).requests - (a as TenantMetrics).requests)
                      .slice(0, 10)
                      .map(([tenant, data]) => {
                        const tenantData = data as TenantMetrics;
                        return (
                        <tr key={tenant}>
                          <td>
                            <Badge variant="outline" size="sm">{tenant}</Badge>
                          </td>
                          <td>{tenantData.requests}</td>
                          <td>
                            <Text color={tenantData.errors > 0 ? 'red' : 'green'} size="sm">
                              {tenantData.errors}
                            </Text>
                          </td>
                          <td>
                            <Text size="xs" color="dimmed">
                              {new Date(tenantData.lastActivity).toLocaleTimeString()}
                            </Text>
                          </td>
                        </tr>
                        );
                      })}
                  </tbody>
                </Table>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>
      </Tabs>

      {/* Export Modal */}
      <Modal
        opened={exportModalOpened}
        onClose={closeExportModal}
        title="Export System Health Data"
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" color="dimmed">
            Export system health metrics and alerts for analysis or reporting.
          </Text>
          
          <JsonInput
            label="Export Data"
            value={exportData}
            minRows={10}
            maxRows={20}
            readOnly
          />
          
          <Group justify="apart">
            <Button variant="light" onClick={closeExportModal}>
              Close
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadExport}
            >
              Download JSON
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default ProductionSystemHealthDashboard;